import { Store, ContextStore, PageStore } from 'bernie-plugin-mobx';
import { SerializedData } from 'bernie-core';
import {
  ContextInput,
  CarDetail,
  RewardsPointSelectionEnum,
  CarAccessorySelectionState
} from '../../src/common/__generated__/api';
import { getBexApiContext } from 'bernie-context';
import { computed, observable } from 'mobx';

export interface IDetailQuery {
  readonly piid?: string;
  readonly offerQualifiers?: string;
  readonly totalPriceShown?: number;
  readonly dailyPriceShown?: number;
  readonly referenceTotalShown?: number;
  readonly searchKey?: string;
  readonly avgCatPR?: number;
  readonly selSpEq?: string;
  readonly date1?: string;
  readonly date2?: string;
  readonly dpln?: string;
  readonly drid1?: string;
  readonly locn?: string;
  readonly loc2?: string;
  readonly time1?: string;
  readonly time2?: string;
  readonly pwaDialog?: string;
  readonly searchUrl?: string;
  readonly needAncillaryBundles?: boolean;
  readonly olat?: string;
  readonly olon?: string;
  readonly dlat?: string;
  readonly dlon?: string;
  readonly useEgtpPath?: boolean;
  readonly period?: string;
}

export interface ISearchQueryInDetailParam {
  readonly locn?: string;
  readonly dpln?: string;
  readonly loc2?: string;
  readonly drid1?: string;
  readonly date1?: string;
  readonly date2?: string;
  readonly time1?: string;
  readonly time2?: string;
  readonly pickupCode?: string;
  readonly dropCode?: string;
  useRewards?: boolean;
  readonly olat?: string;
  readonly olon?: string;
  readonly dlat?: string;
  readonly dlon?: string;
  readonly rfrr?: string;
}

export const searchParamsFromDetail = [
  'locn',
  'dpln',
  'loc2',
  'drid1',
  'date1',
  'date2',
  'time1',
  'time2',
  'pickupCode',
  'dropCode',
  'useRewards',
  'olat',
  'olon',
  'dlat',
  'dlon',
  'rfrr'
];

export class DetailCriteriaStore extends Store {
  private context: ContextInput;
  private page: PageStore;

  public setContext(context: ContextStore) {
    this.context = getBexApiContext(context);
  }

  public setPage(page: PageStore) {
    this.page = page;
  }

  @observable
  public loadingCriteriaChange: boolean = false;

  @observable
  public extraSelectionChanging: boolean = false;

  @observable
  public pointsSelectionChanging: boolean = false;

  @observable
  public rentalProtectionChanging: boolean = false;

  @observable
  public optionToken: string = null;

  @observable
  public optionTokenState: CarAccessorySelectionState = null

  public removedAccessory: string = null;

  public selectedSABCardIndex: number = null;

  public rewardPointsSelection: RewardsPointSelectionEnum = RewardsPointSelectionEnum.DO_NOT_APPLY_REWARD_POINTS;


  @observable
  public detailQuery: IDetailQuery = {};

  @observable
  public searchQueryInDetailParams: ISearchQueryInDetailParam;

  public hydrate(data: SerializedData): void {
    this.context = { ...data.context };
    this.detailQuery = { ...data.detailQuery };
    this.searchQueryInDetailParams = { ...data.searchQueryInDetailParams };
  }

  public setDetailQuery(query: IDetailQuery) {
    const searchUrlParams = {};

    const newDetailQuery = {};
    Object.keys(query).map(key => {
      if (key !== 'prevQuery') {
        const decoded = decodeURIComponent(query[key]);

        newDetailQuery[key] = decoded;
        if (searchParamsFromDetail.includes(key)) {
          searchUrlParams[key] = decoded;
        }

        if (key === 'useRewards') {
          this.rewardPointsSelection = searchUrlParams[key] === 'true' ?  RewardsPointSelectionEnum.APPLY_REWARD_POINTS : RewardsPointSelectionEnum.DO_NOT_APPLY_REWARD_POINTS;
        }

        if (key !== 'selSpEq' && key.match(/sel.*/)) {
          const values = query[key].slice(1, -1);
          const valuesArray = values.split(',');
          let formattedValues;
          if (values && values.length && Array.isArray(valuesArray)) {
            formattedValues = '"' + valuesArray.join('","') + '"';
            searchUrlParams[key] = '[' + formattedValues + ']';
          } else {
            searchUrlParams[key] = '"' + query[key] + '"';
          }
        }
      }
    });

    this.detailQuery = newDetailQuery;
    this.searchQueryInDetailParams = searchUrlParams;
  }

  public getAccessoriesList(): string[] {
    // if selSpEq is not '' or '[]'
    if (this.detailQuery.selSpEq && this.detailQuery.selSpEq.length > 2) {
      try {
        return JSON.parse(this.detailQuery.selSpEq);
      } catch (error) {
        return [];
      }
    }

    return [];
  }

  @computed
  public get detailVariables(): CarDetail.Variables {
    return {
      context          : this.context,
      carDetailContext : {
        carOfferToken            : this.detailQuery.piid,
        rewardPointsSelection    : this.rewardPointsSelection,
        selectedAccessories      : this.getAccessoriesList(),
        continuationContextualId : this.getContinuationContextualId(this.detailQuery),
        optionToken              : this.optionToken,
        optionAction             : this.optionTokenState
      }
    };
  }

  private getContinuationContextualId(detailQuery: IDetailQuery): string {
    return (
      `totalPriceShown=${detailQuery.totalPriceShown}` +
      `&referenceTotalShown=${detailQuery.referenceTotalShown}` +
      `&dailyPriceShown=${detailQuery.dailyPriceShown}` +
      `&offerQualifiers=${detailQuery.offerQualifiers}` +
      `&avgCatPR=${detailQuery.avgCatPR}` +
      `&searchKey=${detailQuery.searchKey}` +
      `&dpln=${detailQuery.dpln}` +
      `&drid1=${detailQuery.drid1}` +
      `&locn=${detailQuery.locn}` +
      `&loc2=${detailQuery.loc2}` +
      `&time1=${detailQuery.time1}` +
      `&time2=${detailQuery.time2}` +
      `&date1=${detailQuery.date1}` +
      `&date2=${detailQuery.date2}` +
      `&needAncillaryBundles=${detailQuery.needAncillaryBundles}` +
      `&useEgtpPath=${detailQuery.useEgtpPath}` +
      `&period=${detailQuery.period}` +
      `&searchUrl=${encodeURIComponent(detailQuery.searchUrl)}`
    );
  }
}
